// Centralized logging system for the application

enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  CRITICAL = 4
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: string;
  data?: any;
  userId?: string;
  sessionId?: string;
  userAgent?: string;
  url?: string;
  stack?: string;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000; // Keep last 1000 logs in memory
  private isDevelopment = import.meta.env.DEV;
  private sessionId = this.generateSessionId();

  private generateSessionId(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  private createLogEntry(level: LogLevel, message: string, context?: string, data?: any): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      data,
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Add stack trace for errors
    if (level >= LogLevel.ERROR) {
      entry.stack = new Error().stack;
    }

    return entry;
  }

  private addLog(entry: LogEntry) {
    this.logs.push(entry);
    
    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console output in development
    if (this.isDevelopment) {
      const style = this.getConsoleStyle(entry.level);
      console.log(
        `%c[${LogLevel[entry.level]}] ${entry.message}`,
        style,
        entry.context ? `Context: ${entry.context}` : '',
        entry.data || ''
      );
    }

    // Send critical errors to external service in production
    if (!this.isDevelopment && entry.level >= LogLevel.ERROR) {
      this.sendToExternalService(entry);
    }
  }

  private getConsoleStyle(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG:
        return 'color: #6B7280; font-weight: normal;';
      case LogLevel.INFO:
        return 'color: #3B82F6; font-weight: normal;';
      case LogLevel.WARN:
        return 'color: #F59E0B; font-weight: bold;';
      case LogLevel.ERROR:
        return 'color: #EF4444; font-weight: bold;';
      case LogLevel.CRITICAL:
        return 'color: #DC2626; font-weight: bold; background: #FEE2E2;';
      default:
        return 'color: #374151; font-weight: normal;';
    }
  }

  private async sendToExternalService(entry: LogEntry) {
    try {
      // In a real application, you would send to services like:
      // - Sentry
      // - LogRocket
      // - DataDog
      // - Custom logging endpoint
      
      // For now, we'll store in localStorage as a fallback
      const existingLogs = JSON.parse(localStorage.getItem('app_error_logs') || '[]');
      existingLogs.push(entry);
      
      // Keep only last 100 error logs in localStorage
      if (existingLogs.length > 100) {
        existingLogs.splice(0, existingLogs.length - 100);
      }
      
      localStorage.setItem('app_error_logs', JSON.stringify(existingLogs));
    } catch (error) {
      console.error('Failed to send log to external service:', error);
    }
  }

  debug(message: string, context?: string, data?: any) {
    this.addLog(this.createLogEntry(LogLevel.DEBUG, message, context, data));
  }

  info(message: string, context?: string, data?: any) {
    this.addLog(this.createLogEntry(LogLevel.INFO, message, context, data));
  }

  warn(message: string, context?: string, data?: any) {
    this.addLog(this.createLogEntry(LogLevel.WARN, message, context, data));
  }

  error(message: string, context?: string, data?: any) {
    this.addLog(this.createLogEntry(LogLevel.ERROR, message, context, data));
  }

  critical(message: string, context?: string, data?: any) {
    this.addLog(this.createLogEntry(LogLevel.CRITICAL, message, context, data));
  }

  // Get logs for debugging or sending to support
  getLogs(level?: LogLevel): LogEntry[] {
    if (level !== undefined) {
      return this.logs.filter(log => log.level >= level);
    }
    return [...this.logs];
  }

  // Clear all logs
  clearLogs() {
    this.logs = [];
    localStorage.removeItem('app_error_logs');
  }

  // Export logs as JSON for debugging
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // Set user context for logs
  setUserContext(userId: string) {
    // Set user context for future logs
    this.currentUserId = userId;
  }

  private currentUserId?: string;

  private createLogEntry(level: LogLevel, message: string, context?: string, data?: any): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      data,
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.currentUserId
    };

    // Add stack trace for errors
    if (level >= LogLevel.ERROR) {
      entry.stack = new Error().stack;
    }

    return entry;
  }
}

// Create singleton instance
export const logger = new Logger();

// Convenience functions
const logDebug = (message: string, context?: string, data?: any) => logger.debug(message, context, data);
const logInfo = (message: string, context?: string, data?: any) => logger.info(message, context, data);
const logWarn = (message: string, context?: string, data?: any) => logger.warn(message, context, data);
const logError = (message: string, context?: string, data?: any) => logger.error(message, context, data);
const logCritical = (message: string, context?: string, data?: any) => logger.critical(message, context, data);

// Global error handler
window.addEventListener('error', (event) => {
  logger.error(
    'Uncaught JavaScript Error',
    'Global Error Handler',
    {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack
    }
  );
});

// Global promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  logger.error(
    'Unhandled Promise Rejection',
    'Global Promise Handler',
    {
      reason: event.reason,
      stack: event.reason?.stack
    }
  );
});

